import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, ExternalLink, Plus, Star, Loader2, Ban, Phone, Voicemail, CalendarCheck } from "lucide-react";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const deepLinks = (cat, zip) => {
  const q = encodeURIComponent(cat);
  return [
    ["Yelp", `https://www.yelp.com/search?find_desc=${q}&find_loc=${zip}`],
    ["Angi", `https://www.google.com/search?q=site%3Aangi.com+${q}+${zip}`],
    ["Facebook", `https://www.facebook.com/search/pages/?q=${q}%20${zip}`],
    ["BBB", `https://www.bbb.org/search?find_country=USA&find_text=${q}&find_loc=${zip}`],
    ["Nextdoor", `https://nextdoor.com/search/?query=${q}`],
    ["Google", `https://www.google.com/search?q=${q}+near+${zip}`],
  ];
};

function ResultRow({ it, checked, onCheck, onAdd, busy, testid }) {
  return (
    <div className="grid gap-2 border-t border-border py-3 sm:grid-cols-[auto_1fr_auto] sm:items-start" data-testid={testid}>
      <Checkbox checked={checked} disabled={!!it.dupe} onCheckedChange={onCheck} data-testid={`${testid}-check`} className="mt-1" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sam-navy">{it.business}</span>
          <span className="inline-flex items-center gap-0.5 text-xs text-sam-slate"><Star className="h-3 w-3 fill-sam-amber text-sam-amber" />{it.rating ?? "–"} · {it.reviews} reviews</span>
          {!it.website && <Badge className="bg-sam-green" data-testid={`${testid}-no-website`}>No website</Badge>}
          {it.dupe && <Badge variant="outline" className="border-sam-amber text-sam-amber" data-testid={`${testid}-dupe`}>{it.dupe}</Badge>}
        </div>
        <div className="text-xs text-sam-slate">{it.address}</div>
        <div className="text-xs text-sam-slate">{it.phone || "No phone"} · {it.website ? <a href={it.website} target="_blank" rel="noreferrer" className="underline">website</a> : "no website"}{it.maps_url && <> · <a href={it.maps_url} target="_blank" rel="noreferrer" className="underline">Maps</a></>}</div>
        <div className="mt-1 text-xs font-medium text-sam-green" data-testid={`${testid}-fit`}>{it.fit}</div>
        <div className="mt-1 flex items-start gap-1 rounded-md bg-slate-50 p-2 text-xs text-sam-navy" data-testid={`${testid}-opener`}>
          <span className="flex-1">{it.opener}</span>
          <button onClick={() => { navigator.clipboard?.writeText(it.opener); toast.success("Opener copied"); }} aria-label="Copy opener" data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <Button size="sm" variant="outline" disabled={!!it.dupe || busy} onClick={onAdd} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Add to campaign</Button>
    </div>
  );
}

export function CategoryResults({ block, zip, onSaved }) {
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const key = slug(block.category);
  const push = async (items) => {
    setBusy(true);
    try {
      const { data } = await api.post("/prospector/campaign", { zip, category: block.category, items: items.map((i) => ({ ...i, category: block.category })) });
      const dup = data.results.filter((r) => !r.saved);
      toast.success(`${data.saved} added to your campaign${dup.length ? ` · ${dup.length} skipped (already on file)` : ""}`);
      setSel({}); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); } finally { setBusy(false); }
  };
  const chosen = block.items.filter((i) => sel[i.place_id]);
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`results-${key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-lg font-bold text-sam-navy">{block.category}</h3>
          {block.taken && <Badge className="bg-slate-500" data-testid={`results-${key}-taken`}>Taken{block.taken_by ? ` · ${block.taken_by}` : ""}</Badge>}
          {block.cached && <Badge variant="outline" data-testid={`results-${key}-cached`}>Shared result · free</Badge>}
        </div>
        {chosen.length > 0 && <Button size="sm" disabled={busy} onClick={() => push(chosen)} className="bg-sam-crimson" data-testid={`results-${key}-bulk`}>{busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Add {chosen.length} to campaign</Button>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {deepLinks(block.category, zip).map(([n, u]) => (
          <a key={n} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-sam-navy hover:bg-sam-navy/5" data-testid={`deeplink-${key}-${n.toLowerCase()}`}>{n}<ExternalLink className="h-3 w-3" /></a>
        ))}
      </div>
      {block.limit && <p className="mt-3 text-sm font-semibold text-sam-crimson" data-testid={`results-${key}-limit`}>Monthly search limit reached — ask your manager</p>}
      {block.taken && !block.items.length && <p className="mt-3 text-sm text-sam-slate">This category is already sold on this card, so it was skipped.</p>}
      {!block.limit && !block.taken && !block.items.length && <p className="mt-3 text-sm text-sam-slate">No 4.0★+ businesses found. Try a bigger radius or the links above.</p>}
      {block.items.map((it, i) => (
        <ResultRow key={it.place_id} it={it} busy={busy} testid={`result-${key}-${i}`} checked={!!sel[it.place_id]}
          onCheck={(v) => setSel({ ...sel, [it.place_id]: !!v })} onAdd={() => push([it])} />
      ))}
    </div>
  );
}

const EMPTY = { business: "", contact_name: "", phone: "", website: "", email: "", source: "Yelp", notes: "", category: "" };

export function QuickAdd({ zip, sources, categories, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/prospector/quick-add", { ...f, zip });
      if (data.saved) { toast.success(data.call_needed ? "Added to your call list (no email)" : "Added — the invite email goes out from your campaign"); setF(EMPTY); onSaved?.(); } else toast.warning(data.dupe);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="rounded-xl border border-border bg-white p-4 space-y-2" data-testid="quick-add-form">
      <h3 className="font-display text-lg font-bold text-sam-navy">Quick add</h3>
      <p className="text-xs text-sam-slate">Found a business on Yelp, Facebook or elsewhere? Add it here by hand. Quick add never uses your search limit.</p>
      <Input placeholder="Business name" value={f.business} onChange={set("business")} data-testid="qa-business" />
      <Input placeholder="Owner / contact name" value={f.contact_name} onChange={set("contact_name")} data-testid="qa-contact-name" />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="qa-phone" />
        <Input placeholder="Email" value={f.email} onChange={set("email")} data-testid="qa-email" />
      </div>
      <Input placeholder="Website" value={f.website} onChange={set("website")} data-testid="qa-website" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.source} onValueChange={(v) => setF({ ...f, source: v })}>
          <SelectTrigger data-testid="qa-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger data-testid="qa-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{categories.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea placeholder="Notes" rows={2} value={f.notes} onChange={set("notes")} data-testid="qa-notes" />
      <Button type="submit" disabled={busy || !zip} className="w-full bg-sam-navy" data-testid="qa-submit">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Add to campaign</Button>
    </form>
  );
}

const LABEL = { new: "New", call_needed: "Call needed", contacted: "Contacted", replied: "Replied", booked: "Booked", sold: "Sold", not_interested: "Not interested" };

function CallOutcomes({ p, i, onChange }) {
  const [busy, setBusy] = useState(false);
  const log = async (outcome) => {
    setBusy(true);
    try { await api.post(`/prospector/prospects/${p.id}/call`, { outcome }); toast.success(outcome === "booked" ? "Booked — nice work!" : "Voicemail logged"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't log the call"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => log("voicemail")} data-testid={`call-voicemail-${i}`}><Voicemail className="h-3.5 w-3.5 mr-1" />Left voicemail</Button>
      <Button size="sm" className="h-8 bg-sam-crimson text-xs" disabled={busy} onClick={() => log("booked")} data-testid={`call-booked-${i}`}><CalendarCheck className="h-3.5 w-3.5 mr-1" />Booked</Button>
      {p.last_call && <span className="text-[11px] text-sam-slate" data-testid={`call-last-${i}`}>{p.last_call.outcome} · {new Date(p.last_call.at).toLocaleDateString()} · {p.call_attempts} call{p.call_attempts === 1 ? "" : "s"}</span>}
    </div>
  );
}

export function CallList({ rows, statuses, onChange }) {
  const calls = rows.filter((p) => !p.email && !p.dnc);
  if (!calls.length) return <p className="text-sm text-sam-slate" data-testid="call-list-empty">No calls needed. Every prospect without an email shows up here.</p>;
  return (
    <div className="divide-y divide-border rounded-xl border border-border bg-white" data-testid="call-list">
      {calls.map((p, i) => (
        <div key={p.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`call-row-${i}`}>
          <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
            <div className="font-semibold text-sam-navy">{p.business}{p.name ? ` · ${p.name}` : ""}</div>
            <div className="text-xs text-sam-slate">{p.category} · ZIP {p.zip}</div>
          </div>
          {p.phone ? <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 rounded-full bg-sam-green px-3 py-1.5 text-sm font-bold text-white" data-testid={`call-link-${i}`}><Phone className="h-3.5 w-3.5" />{p.phone}</a> : <span className="text-xs text-sam-crimson">No phone</span>}
          <CallOutcomes p={p} i={i} onChange={onChange} />
          <Select value={p.status} onValueChange={async (v) => { try { await api.patch(`/prospector/prospects/${p.id}`, { status: v }); onChange?.(); } catch { toast.error("Couldn't update"); } }}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`call-status-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{LABEL[s] || s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

export function CampaignList({ rows, statuses, onChange }) {
  const upd = async (id, body) => {
    try { await api.patch(`/prospector/prospects/${id}`, body); onChange?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  if (!rows.length) return <p className="text-sm text-sam-slate" data-testid="campaign-empty">No prospects yet. Add businesses from your search or Quick add.</p>;
  return (
    <div className="divide-y divide-border rounded-xl border border-border bg-white" data-testid="campaign-list">
      {rows.map((p, i) => (
        <div key={p.id} className={`flex flex-wrap items-center gap-3 p-3 ${p.dnc ? "opacity-60" : ""}`} data-testid={`campaign-row-${i}`}>
          <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
            <div className="font-semibold text-sam-navy">{p.business} {p.dnc && <Badge className="ml-1 bg-sam-crimson" data-testid={`campaign-dnc-badge-${i}`}>Do not contact</Badge>}</div>
            <div className="text-xs text-sam-slate">{p.category} · ZIP {p.zip} · {p.source} · {p.phone || p.email || p.website}</div>
          </div>
          <Select value={p.status} onValueChange={(v) => upd(p.id, { status: v })}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`campaign-status-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{LABEL[s] || s}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => upd(p.id, { dnc: !p.dnc })} data-testid={`campaign-dnc-${i}`}><Ban className="h-3.5 w-3.5 mr-1" />{p.dnc ? "Allow contact" : "Do not contact"}</Button>
        </div>
      ))}
    </div>
  );
}
